import {
  array,
  boolean,
  InferOutput,
  literal,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  union,
  ValiError,
} from 'valibot';
import {
  AlbumItem,
  AlbumItemSchema,
  ArtistItem,
  ArtistItemSchema,
  SongItemSchema,
} from '../apiSchema.ts';
import { AMusicError } from '../utils.ts';

export const ResourceRelationshipRequestArgsSchema = object({
  type: union([literal('album'), literal('track')]),
  id: string(),
  relationship: union([literal('albums'), literal('artists')]),
  isLibrary: optional(boolean()),
});

export type ResourceRelationshipRequestArgs = InferOutput<
  typeof ResourceRelationshipRequestArgsSchema
>;

export const LibrarySongsRelationshipAPISchema = pipe(
  object({
    data: object({
      data: array(SongItemSchema),
    }),
  }),
  transform(({ data: { data } }) => {
    if (!Array.isArray(data)) {
      throw new TypeError('Expected data to be an array');
    }

    const res = data[0];
    return res.relationships;
  }),
);

export type LibrarySongsRelationshipResponse = InferOutput<
  typeof LibrarySongsRelationshipAPISchema
>;

export const LibraryAlbumRelationshipAPISchema = pipe(
  object({
    data: object({
      data: array(AlbumItemSchema),
    }),
  }),
  transform(({ data: { data } }) => {
    if (!Array.isArray(data)) {
      throw new TypeError('Expected data to be an array');
    }

    const res = data[0];
    return res.catalog;
  }),
);

export type LibraryAlbumRelationshipResponse = InferOutput<
  typeof LibraryAlbumRelationshipAPISchema
>;

export const CatalogRelationshipAPISchema = pipe(
  object({
    data: object({
      data: array(union([ArtistItemSchema, AlbumItemSchema])),
    }),
  }),
  transform(({ data: { data } }) => {
    if (!Array.isArray(data)) {
      throw new TypeError('Expected data to be an array');
    }
    const res = data[0];
    return res;
  }),
);

const resource = {
  album: 'albums',
  albums: 'albums',
  track: 'songs',
  tracks: 'songs',
};

const NotFoundError = (message: string): AMusicError => {
  return new AMusicError(message, AMusicError.NOT_FOUND);
};

export const $getResourceRelationship = async (
  player: MusicKit.MusicKitInstance,
  args: ResourceRelationshipRequestArgs,
): Promise<AlbumItem | ArtistItem> => {
  const p = safeParse(
    ResourceRelationshipRequestArgsSchema,
    args,
  );
  if (!p.success) {
    throw new ValiError(p.issues);
  }

  const { type, id, relationship, isLibrary = true } = p.output;
  const endpoint = isLibrary
    ? `/v1/me/library/${resource[type]}/${id}`
    : `/v1/catalog/${player.storefrontId}/${
      resource[type]
    }/${id}/${relationship}`;

  if (isLibrary && type === 'track') {
    // /v1/me/library/{songs}/{id}?include=catalog,{albums | artists}
    const response = await player.api.music(
      endpoint,
      {
        include: ['catalog', relationship],
      },
    );
    const parsedRes = safeParse(
      LibrarySongsRelationshipAPISchema,
      response,
    );
    if (!parsedRes.success) {
      throw new ValiError(parsedRes.issues);
    }
    const res = parsedRes.output;
    if (!res) {
      throw NotFoundError(
        `No data found for the specified resource relationship: request args=${
          JSON.stringify(
            args,
          )
        }`,
      );
    }

    const libraryAlbum = res.album;
    const libraryArtist = res.artist;

    if (relationship === 'albums') {
      const item = libraryAlbum?.catalog?.album;
      if (!item) {
        throw NotFoundError(
          `No catalog album found for the specified resource relationship: request args=${
            JSON.stringify(
              args,
            )
          }`,
        );
      }
      return item as AlbumItem;
    } else if (relationship === 'artists') {
      const item = libraryArtist?.catalog?.artist;
      if (!item) {
        throw NotFoundError(
          `No catalog artist found for the specified resource relationship: request args=${
            JSON.stringify(
              args,
            )
          }`,
        );
      }
      return item as ArtistItem;
    }

    throw new TypeError(
      `Invalid relationship type: ${relationship}. Expected 'albums' or 'artists'.`,
    );
  } else if (isLibrary && type === 'album') {
    // /v1/me/library/{albums}/{id}?include=catalog,artists
    const response = await player.api.music(
      endpoint,
      {
        include: ['catalog', relationship],
      },
    );
    const parsedRes = safeParse(
      LibraryAlbumRelationshipAPISchema,
      response,
    );
    if (!parsedRes.success) {
      throw new ValiError(parsedRes.issues);
    }
    const res = parsedRes.output;
    if (!res) {
      throw NotFoundError(
        `No data found for the specified resource relationship: request args=${
          JSON.stringify(args)
        }`,
      );
    }

    const catalogAlbum = res.album;
    const catalogArtist = res.artist;
    if (relationship === 'albums') {
      if (!catalogAlbum) {
        throw NotFoundError(
          `No catalog album found for the specified resource relationship: request args=${
            JSON.stringify(args)
          }`,
        );
      }
      return catalogAlbum as AlbumItem;
    } else if (relationship === 'artists') {
      if (!catalogArtist) {
        throw NotFoundError(
          `No catalog artist found for the specified resource relationship: request args=${
            JSON.stringify(args)
          }`,
        );
      }
      return catalogArtist as ArtistItem;
    }

    throw new TypeError(
      `Invalid relationship type: ${relationship}. Expected 'albums' or 'artists'.`,
    );
  } else {
    // /v1/catalog/{storefrontId}/{songs | albums}/{id}/{albums | artists}?limit=1

    // Note: The limit is set to 1 to retrieve a single item.
    // This is because the API returns an array of items, but we only need one.
    // The relationship will be included in the response.
    const response = await player.api.music(
      endpoint,
      {
        limit: 1,
      },
    );
    const parsedRes = safeParse(
      CatalogRelationshipAPISchema,
      response,
    );
    if (!parsedRes.success) {
      throw new ValiError(parsedRes.issues);
    }
    if (!parsedRes.output) {
      throw NotFoundError(
        `No data found for the specified resource relationship: request args=${
          JSON.stringify(args)
        }`,
      );
    }
    const res = parsedRes.output;
    return res;
  }
};
